import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Typeahead, Token} from 'react-bootstrap-typeahead';
import {capitalize, snakeCase} from 'lodash';
import {FormGroup, ControlLabel, HelpBlock, InputGroup} from 'react-bootstrap';
import {nodeId} from 'libs';

import 'react-bootstrap-typeahead/css/Typeahead.css';

import './styles.scss';

export default class MultiSelect extends Component {
  constructor(props) {
    super(props);

    this._inputText = '';
    this._optionCount = 0;
    this._printErrors = this._printErrors.bind(this);
  }

  _printErrors(error) {
    return error && error.messages.map((each) => {
      return (
        <HelpBlock
          key={nodeId('multiselect-error')}
          className={'text-danger error'}
        >
          {each.text}
        </HelpBlock>
      );
    });
  }

  /**
   * Append the nestLevel for each node so that a list can be properly indented
   *
   * @param {Array} list contains a childid and a parentid to be parsed for depth
   * @param {String} childId is the name of the key for the childid in the list
   * @param {String} parentId is the name of the key for the parentid in the list
   * @returns {Array} [...list, nestLevel]
   */
  _appendDepth(list, childId, parentId) {
    const dictionary = new Map();

    list.forEach((item) => {
      dictionary.set(item[childId], {...item, depth: 0});
    });

    list.forEach((item) => {
      let parentid = item[parentId];
      let depth = 0;

      while (parentid) {
        parentid = (dictionary.get(parentid) || {})[parentId];
        depth++;

        // Escape
        if (depth > 10) {
          parentid = 0;
        }
      }

      dictionary.set(item[childId], {...item, depth});
    });

    // Update list
    return list.map((item) => {
      return {...item, nestLevel: dictionary.get(item[childId])['depth']};
    });
  }

  render() {
    const {
      label,
      placeholder,
      handleChange,
      type,
      error,
      labelKey,
      selected,
      selectAll,
      multiple,
      nullPrefix,
      hasChildren,
      filterByKeys = [],
      renderCustomCategoryToken,
      keepFocusAfterSelect,
      renderMenu,
      handleFocus,
      handleBlur,
      isFetching,
      clearOnSelect,
      elemsBeforeTypeahead,
      elemsAfterTypehead,
      typeAheadProps,
      renderElemsInsideInputGroup,
      selectHintOnEnter,
      clearButton,
      renderToken,
      renderMenuItemChildren,
      renderInput,
    } = this.props;

    let {options} = this.props;

    const typeAheadPropsInternal = {...typeAheadProps};

    const nullOption = (nullPrefix === '')
      ? {[labelKey]: capitalize(`All ${label}`), id: null}
      : {[labelKey]: capitalize(nullPrefix), id: null};

    if (renderToken) {
      typeAheadPropsInternal.renderToken = renderToken;
    }

    if (renderInput) {
      typeAheadPropsInternal.renderInput = renderInput;
    }

    if (renderMenuItemChildren) {
      typeAheadPropsInternal.renderMenuItemChildren = renderMenuItemChildren;
    }

    if (renderCustomCategoryToken) {
      typeAheadPropsInternal.renderToken = (option, props) => (
        <Token key={option.id} title={option.keywords} onRemove={props.onRemove}>
          {option[labelKey]}
        </Token>
      );
    }

    if (filterByKeys.length) {
      typeAheadPropsInternal.filterBy = [labelKey, ...filterByKeys];
    }

    // Append nestLevel to indent options
    switch (labelKey) {
      case 'plantypename':
        options = this._appendDepth(options, 'id', 'parentid');
    }

    if (labelKey === 'categoryname' || hasChildren) {
      typeAheadPropsInternal.renderMenuItemChildren = this._renderCategoryMenuItem;
      typeAheadPropsInternal.filterBy = [labelKey, 'parentname', ...filterByKeys];
    } else {
      typeAheadPropsInternal.renderMenuItemChildren = this._renderMenuItem;
    }

    if (typeof renderMenu == 'function') {
      typeAheadPropsInternal.renderMenu = renderMenu;
    }

    if (typeof handleBlur == 'function') {
      typeAheadPropsInternal.onBlur = () => {
        const instance = this._typeahead.getInstance();
        handleBlur(instance);
      };
    }

    if (typeof handleFocus == 'function') {
      typeAheadPropsInternal.onFocus = () => {
        const instance = this._typeahead.getInstance();
        handleFocus(instance);
      };
    }

    if (keepFocusAfterSelect) {
      typeAheadPropsInternal.onInputChange = (text) => {
        this._inputText = text;
      };
    }

    let selectOptions = options;
    if (selectAll) {
      selectOptions = [nullOption, ...options];
    }

    if (options.length < 1) {
      typeAheadPropsInternal.disabled = true;
    }

    typeAheadPropsInternal.selectHintOnEnter = selectHintOnEnter;

    const noOptionsPlaceholder = isFetching ? 'Loading...' : 'No options available.';

    this._optionCount = !selected ? 0 : selected.length;

    const typeAhead =
    <Typeahead
      id={nodeId()}
      ref={(ref) => this._typeahead = ref}
      multiple={multiple}
      clearButton={clearButton}
      options={selectOptions}
      selected={selected === null ? [nullOption] : selected}
      placeholder={options.length > 0 ? placeholder : noOptionsPlaceholder}
      onChange={(selected) => {
        handleChange(selected, type);

        if (!this._typeahead) {
          return;
        }

        const instance = this._typeahead.getInstance();
        if (keepFocusAfterSelect) {
          if (selected.length < this._optionCount) {
            this._inputText = '';
          }
          instance.setState({text: this._inputText, showMenu: true});
        }
        if (clearOnSelect) {
          instance.setState({text: '', showMenu: true});
        }
      }}
      labelKey={labelKey}
      {...typeAheadPropsInternal}
    />;

    const typeAheadWithPrependedAndAppendedElems =
    <div>
      {elemsBeforeTypeahead}
      {typeAhead}
      {elemsAfterTypehead}
    </div>;

    const inputGroup =
      <InputGroup style={{width: '100%'}}>
        {typeAheadWithPrependedAndAppendedElems}
      </InputGroup>;

    return (

      <FormGroup data-component={'MultiSelect'} data-label={snakeCase(label)} validationState={error ? 'error' : null}>
        {label &&
          <ControlLabel htmlFor={typeAheadPropsInternal.inputProps?.id ?? undefined}>{label}</ControlLabel>
        }
        {renderElemsInsideInputGroup ? inputGroup : typeAheadWithPrependedAndAppendedElems}
        {error &&
          this._printErrors(error)
        }
      </FormGroup>


    );
  }

  _renderCategoryMenuItem(option, props) {
    const {
      labelKey,
    } = props;

    const optionHasKeywords = Object.hasOwn(option, 'keywords');

    if (option.id === null) {
      return (
        <div key={option.id} title={optionHasKeywords ? option.keywords : null}>
          <strong>{option[labelKey]}</strong>
        </div>
      );
    } else if ((typeof option.id === 'string') && (option.id.indexOf('cat') !== -1)) {
      if (option.id.indexOf('sssubcat') !== -1) {
        option.nestLevel = 3;
      } else if (option.id.indexOf('ssubcat') !== -1) {
        option.nestLevel = 2;
      } else if (option.id.indexOf('subcat') !== -1) {
        option.nestLevel = 1;
      } else {
        option.nestLevel = 0;
      }
    }

    return (
      <div key={option.id} title={optionHasKeywords ? option.keywords : null} style={{paddingLeft: `${option.nestLevel * 30}px`}}>
        <strong>{option[labelKey]}</strong>
        {option.parentname &&
          <div>
            <small>Parent: {option.parentname}</small>
          </div>
        }
      </div>
    );
  }

  _renderMenuItem(option, props) {
    const {
      labelKey,
    } = props;

    let label = '';
    if (typeof option === 'string') {
      label = option;
    } else {
      if (labelKey) {
        label = option[labelKey];
      } else {
        label = option.label;
      }
    }

    return (
      <span title={option.keywords}>{label}</span>
    );
  }
}

MultiSelect.defaultProps = {
  labelKey: 'label',
  error: false,
  multiple: true,
  selectAll: false,
  nullPrefix: '',
  placeholder: 'Select an option',
  hasChildren: false,
  handleFocus: false,
  handleBlur: false,
  selectHintOnEnter: true,
};

MultiSelect.propTypes = {
  isFetching: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
  ]),
  selectHintOnEnter: PropTypes.bool,
  typeAheadProps: PropTypes.object,
  renderElemsInsideInputGroup: PropTypes.bool,
  renderToken: PropTypes.func,
  renderMenuItemChildren: PropTypes.func,
  renderInput: PropTypes.func,
  elemsBeforeTypeahead: PropTypes.object,
  elemsAfterTypehead: PropTypes.object,
  selectAll: PropTypes.bool,
  multiple: PropTypes.bool,
  clearButton: PropTypes.bool,
  hasChildren: PropTypes.bool,
  keepFocusAfterSelect: PropTypes.bool,
  clearOnSelect: PropTypes.bool,
  filterByKeys: PropTypes.array,
  renderCustomCategoryToken: PropTypes.bool,
  nullPrefix: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  labelKey: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  options: PropTypes.array.isRequired,
  selected: PropTypes.array,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  handleFocus: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  renderMenu: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
};
